import { defineComponent as _defineComponent } from 'vue'
import {
  FilterKeyValueType,
  IFilter,
  IOption,
} from "@/domain/entities/filter/filter.types";
import {
  GetOptions,
  HttpBaseFilter,
  OptionMapper,
} from "@/repositories/lineInfo/lineInfoRepository.types";
import { useLineStore } from "@/stores/lineStore";
import {
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  ShowErrorModal,
  ShowToastMessage,
} from "@/types";
import { PropType, computed, inject, onMounted, ref } from "vue";

import TextInput from "@/components/shared/inputs/TextInput.vue";

import FooterButton from "@/components/shared/button/FooterButton.vue";
import { INewRouteApplicationDetail } from "@/domain/entities/bbs";
import { useBbsStore } from "@/stores/bbsStore";
import router from "@/router";
import { useRoute } from "vue-router";
import LabeledCustomSelect from "@/components/shared/selects/LabeledCustomSelect.vue";
import VocEditGuideMessage from "../voc/VocEditGuideMessage.vue";
import BaseTextarea from "@/components/shared/inputs/base/BaseTextarea.vue";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";


const __default__ = {
  name: "NewRouteApplicationEdit",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  createMode: {
    type: Boolean,
    default: false,
  },
  applicationDetail: {
    type: Object as PropType<INewRouteApplicationDetail>,
  },
},
  emits: ["onClickAddressInput", "onCompleteEdit", "onUpdateApplicationDetail"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props;

const emit = __emit;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const route = useRoute();
const lineStore = useLineStore();
const bbsStore = useBbsStore();

const scrollContainer = ref<HTMLElement>();

const address = computed(() => {
  if (!props.applicationDetail) return "";
  const { roadAddress, dongAddress } = props.applicationDetail;
  return roadAddress?.length ? roadAddress : dongAddress;
});

const footerBtnTitle = computed(() => (props.createMode ? "신청하기" : "수정"));
const isValid = computed(
  () =>
    !!props.applicationDetail?.campusCode &&
    !!props.applicationDetail?.workTimeCode &&
    !!props.applicationDetail?.talkLineWeekdayCode &&
    !!props.applicationDetail?.lineDirectionCode &&
    (!!props.applicationDetail?.roadAddress ||
      !!props.applicationDetail?.dongAddress)
);

const emitOnClickAddressInput = () => {
  emit("onClickAddressInput");
};

// filter 정의
const campusFilter = ref<IFilter>();
const workTimeFilter = ref<IFilter>();
const weekdayFilter = ref<IFilter>();
const directionFilter = ref<IFilter>();

const filterMapper: OptionMapper<HttpBaseFilter> = (data) => {
  return {
    key: data.uid,
    name: data.name,
  };
};

const weekdayMapper: OptionMapper<HttpBaseFilter> = (data) => {
  return {
    key: data.uid,
    name: data.name,
  };
};

const getOptionsByCode = (customCode: string) => {
  const getOptions: GetOptions<HttpBaseFilter> = (response) => {
    const targetCodeGroup = response.data?.find(
      (codeGroup) => codeGroup.codeGroup === customCode
    );
    return targetCodeGroup?.customCodes ?? [];
  };

  return getOptions;
};

const getFilter = async (
  pathParam: string,
  key: string,
  multiple = false,
  optionMapper: OptionMapper<HttpBaseFilter>,
  label?: string,
  dummyOptionName?: string,
  defaultOptionCode?: string,
  getOptions?: GetOptions<any>
) => {
  const result = await lineStore.getFilter(
    pathParam,
    key,
    FilterKeyValueType.FilterKey_OptionKey,
    optionMapper,
    false,
    multiple,
    label,
    dummyOptionName,
    defaultOptionCode,
    undefined,
    undefined,
    undefined,
    getOptions
  );
  if (!result.isSuccess) {
    showToastMessage(`${label} 필터 목록을 가져오지 못했습니다`);
    return;
  }
  return result.data;
};

const getFilters = async () => {
  const path = `/system/custom-code?codeGroup=`;

  const [campusResult, workTimeResult, weekdayResult, lineDirectionResult] =
    await Promise.all([
      getFilter(
        `${path}CPS`,
        "campusCode",
        false,
        filterMapper,
        "캠퍼스",
        undefined,
        props.applicationDetail?.campusCode,
        getOptionsByCode("CPS")
      ),
      getFilter(
        `${path}WTM`,
        "workTimeCode",
        false,
        filterMapper,
        "노선형태",
        undefined,
        props.applicationDetail?.workTimeCode,
        getOptionsByCode("WTM")
      ),
      getFilter(
        `${path}LRD`,
        "talkLineWeekdayCode",
        false,
        weekdayMapper,
        "통상구분",
        undefined,
        props.applicationDetail?.talkLineWeekdayCode,
        getOptionsByCode("LRD")
      ),
      getFilter(
        `${path}LDR`,
        "lineDirectionCode",
        false,
        filterMapper,
        "방향",
        undefined,
        props.applicationDetail?.lineDirectionCode,
        getOptionsByCode("LDR")
      ),
    ]);

  campusFilter.value = campusResult;
  workTimeFilter.value = workTimeResult;
  weekdayFilter.value = weekdayResult;
  directionFilter.value = lineDirectionResult;
};

const updateSelectOption = (filter: IFilter, option: IOption) => {
  const updateData: Record<string, string> = {};
  if (filter.type === FilterKeyValueType.FilterKey_OptionKey) {
    updateData[filter.key ?? ""] = option.key;
  }
  emitOnUpdateApplicationDetail(updateData);
};

const updateData = (value: string, key: string) => {
  const updateData: Record<string, string> = {};
  updateData[key] = value;
  emitOnUpdateApplicationDetail(updateData);
};

const emitOnUpdateApplicationDetail = (updateData: Record<string, string>) => {
  emit("onUpdateApplicationDetail", updateData);
};

const handleError = (title: string, result: any) => {
  showErrorModal(
    title,
    result.errorMessage,
    result.resultCode,
    result.errorTime
  );
};

const applyNewRoute = () => {
  if (props.createMode) {
    createNewRouteApplication();
  } else {
    updateNewRouteApplication();
  }
};

const createNewRouteApplication = async () => {
  if (!props.applicationDetail) return;

  const createResult = await bbsStore.applyNewRouteApplication(
    props.applicationDetail
  );

  if (!createResult.isSuccess) {
    handleError("신규 노선/정류장 신청 오류", createResult);
    return;
  }
  showToastMessage("신규 노선/정류장 신청이 완료되었습니다");
  route.meta.willGoBack = true;
  router.back();
};

const updateNewRouteApplication = async () => {
  if (!props.applicationDetail?.uid) return;

  const updateResult = await bbsStore.editNewRouteApplication(
    props.applicationDetail.uid,
    props.applicationDetail
  );

  if (!updateResult.isSuccess) {
    handleError("신규노선 수정 오류", updateResult);
    return;
  }
  showToastMessage("수정되었습니다");
  emit("onCompleteEdit");
};

onMounted(async () => {
  getFilters();
});

const __returned__ = { props, emit, showErrorModal, showToastMessage, route, lineStore, bbsStore, scrollContainer, address, footerBtnTitle, isValid, emitOnClickAddressInput, campusFilter, workTimeFilter, weekdayFilter, directionFilter, filterMapper, weekdayMapper, getOptionsByCode, getFilter, getFilters, updateSelectOption, updateData, emitOnUpdateApplicationDetail, handleError, applyNewRoute, createNewRouteApplication, updateNewRouteApplication, TextInput, FooterButton, LabeledCustomSelect, VocEditGuideMessage, BaseTextarea, ScrollTopControl }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})